import { Component, Input } from "@angular/core";
import { GameSettingsService } from "../game-settings.service";
import { environment } from "@env/environment";
import { 
  getTitleTextStyle, 
  getCategoryTextStyle, 
  getTitlePositionStyle, 
  getCategoryPositionStyle,
  getTextShadow,
  isSameEdge,
  getSameEdgeContainerStyle
} from "@app/shared/utils/game-card-styles.util";
import { takeUntil } from "rxjs/operators";
import { ConfirmationDialogComponent } from "../../confirmation-dialog/confirmation-dialog.component";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Subject } from "rxjs";

@Component({
  selector: "similar-games",
  templateUrl: "./similar-games.component.html",
  styleUrls: ["./similar-games.component.scss"],
})
export class SimilarGamesComponent {
  @Input("categoryId") categoryId = "";
  @Input("gameId") gameId = "";

  selectedSimilarGame: any = "";

  s3BucketUrl: string = environment.s3BucketUrl;

  // Private
  private _unsubscribeAll: Subject<any>;

  fileNameDialogRef: MatDialogRef<ConfirmationDialogComponent>;

  constructor(
    public gameSettingsService: GameSettingsService,
    private dialog: MatDialog
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  onSimilarGameSelectionChange = ($event) => {
    const selectedGame = $event.value;

    console.log("selectedGame....", selectedGame);

    if (selectedGame) {
      const isGameAlreadySelected =
        this.gameSettingsService.selectedSimilarGames.some(
          (game) => game.id === selectedGame.id
        );

      if (!isGameAlreadySelected) {
        this.gameSettingsService.selectedSimilarGames.push(selectedGame);
      }
    }

    this.selectedSimilarGame = ""; // Resetting for the next selection
  };

  onSimilarGameRemove = (game) => {
    this.fileNameDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: "Are you sure you want to remove this Similar Game?",
      },
    });

    this.fileNameDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        // category deleting on selecting yes from confirmation dialog
        if (result) {
          this.gameSettingsService.selectedSimilarGames =
            this.gameSettingsService.selectedSimilarGames
              .filter((selectedGame) => selectedGame.id !== game.id)
              .slice(); // Use slice to create a shallow copy for immutability
        }
      });
  };

  getNextPageData = ($event) => {
    // console.log("$event....", $event);
  };

  private _styleCache = new Map<string, any>();

  private getCachedStyle(game: any, keySuffix: string, computeFn: () => any): any {
    if (!game || !game.id) return computeFn();
    const key = `${game.id}_${keySuffix}_${JSON.stringify([
      game.titleOpt,
      game.categoryOpt,
      game.shadowOpt,
      game.gameMenuArt,
      game.title,
      game.category,
      game.isGameMenuToolbar
    ])}`;
    if (this._styleCache.has(key)) {
      return this._styleCache.get(key);
    }
    const res = computeFn();
    this._styleCache.set(key, res);
    return res;
  }

  /**
   * Determines the background color based on specific conditions.
   * @returns {string} The calculated background color.
   */
  setBackgroundColor(game): string {
    return this.getCachedStyle(game, 'bgColor', () => {
      if (game.isGameMenuToolbar) {
        return "#dcd7d7";
      } else if (game.gameMenuArt && game.gameMenuArt.menuArtOption === "option_0") {
        return "transparent";
      } else if (
        game.gameMenuArt &&
        (game.gameMenuArt.menuArtOption === "option_3" ||
          game.gameMenuArt.menuArtOption === "option_4")
      ) {
        return "rgba(255, 255, 255, 0.5)";
      } else {
        return "transparent";
      }
    });
  }

  getTitleTextStyle(game: any): { [key: string]: string } {
    return this.getCachedStyle(game, 'titleTextStyle', () => getTitleTextStyle(game));
  }

  isSameEdge(game: any): boolean {
    return isSameEdge(game);
  }

  getSameEdgeContainerStyle(game: any, position: 'top' | 'bottom'): { [key: string]: string } {
    return this.getCachedStyle(game, `sameEdge_${position}`, () => getSameEdgeContainerStyle(game, position));
  }

  getTitlePositionStyle(game: any): { [key: string]: string } {
    return this.getCachedStyle(game, 'titlePosStyle', () => getTitlePositionStyle(game));
  }

  getCategoryTextStyle(game: any): { [key: string]: string } {
    return this.getCachedStyle(game, 'catTextStyle', () => getCategoryTextStyle(game));
  }

  getCategoryPositionStyle(game: any): { [key: string]: string } {
    return this.getCachedStyle(game, 'catPosStyle', () => getCategoryPositionStyle(game));
  }

  getShadowStyle(game: any): string {
    return this.getCachedStyle(game, 'shadowStyle', () => getTextShadow(game));
  }
}
